import { FormEvent, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  AppUserManagement,
  createUser,
  deleteUser,
  listRoles,
  listUsers,
  RoleManagement,
  updateUser,
} from '../../services/accessControlApi';
import { listActivePermissionGroups, PermissionGroup } from '../../services/permissionGroupsApi';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { PasswordInput } from '../../components/ui/PasswordInput';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { TableActionLink } from '../../components/ui/TableActionLink';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';

const HIERARCHICAL_PROFILE_CODES = new Set([
  'admin',
  'gerente',
  'colaborador',
  'editor',
  'visitante',
  'suporte',
  'cliente',
]);

export function UsersAdminPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('access-control.users');
  const [users, setUsers] = useState<AppUserManagement[]>([]);
  const [profiles, setProfiles] = useState<RoleManagement[]>([]);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    accessProfileId: '',
    permissionGroupIds: [] as string[],
    isActive: true,
  });
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();

  const defaultProfileId = useMemo(() => profiles[0]?.id ?? '', [profiles]);

  const formTitle = editingId ? 'Editar colaborador' : 'Novo colaborador';
  const editingFormSummary = editingId
    ? `Editando: ${form.name || form.email || 'colaborador'}`
    : undefined;

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const [userResult, roleResult, groupResult] = await Promise.all([
        listUsers({ page: targetPage, pageSize }),
        listRoles({ page: 1, pageSize: 200 }),
        listActivePermissionGroups(),
      ]);
      const hierarchicalProfiles = roleResult.items.filter((role) =>
        HIERARCHICAL_PROFILE_CODES.has(role.code.toLowerCase()),
      );

      setUsers(userResult.items);
      setTotalCount(userResult.totalCount);
      setPage(userResult.page);
      setProfiles(hierarchicalProfiles);
      setPermissionGroups(groupResult);

      if (!form.accessProfileId && hierarchicalProfiles[0]) {
        setForm((current) => ({ ...current, accessProfileId: hierarchicalProfiles[0].id }));
      }
    } catch {
      toast.error('Falha ao carregar usuários.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  function resetForm(): void {
    setEditingId(null);
    setForm({
      name: '',
      email: '',
      password: '',
      accessProfileId: defaultProfileId,
      permissionGroupIds: [],
      isActive: true,
    });
  }

  function togglePermissionGroup(groupId: string): void {
    setForm((current) => {
      const selected = new Set(current.permissionGroupIds);
      if (selected.has(groupId)) {
        selected.delete(groupId);
      } else {
        selected.add(groupId);
      }
      return { ...current, permissionGroupIds: Array.from(selected) };
    });
  }

  function startEdit(user: AppUserManagement): void {
    expandForm();
    setEditingId(user.id);
    setForm({
      name: user.name,
      email: user.email,
      password: '',
      accessProfileId: user.accessProfileId ?? user.roleIds[0] ?? '',
      permissionGroupIds: user.permissionGroupIds,
      isActive: user.isActive,
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    if (!form.accessProfileId && form.permissionGroupIds.length === 0) {
      toast.error('Selecione um perfil hierárquico ou ao menos um grupo de permissões.');
      return;
    }

    const payload = {
      name: form.name,
      email: form.email,
      password: form.password || undefined,
      accessProfileId: form.accessProfileId || undefined,
      roleIds: form.accessProfileId ? [form.accessProfileId] : [],
      permissionGroupIds: form.permissionGroupIds,
      isActive: form.isActive,
    };

    try {
      if (editingId) {
        await updateUser(editingId, payload);
        toast.success('Usuário atualizado.');
        collapseForm(resetForm);
      } else {
        if (!form.password) {
          toast.error('Senha é obrigatória para novo usuário.');
          return;
        }
        await createUser({
          ...payload,
          password: form.password,
        });
        toast.success('Usuário criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao salvar usuário.');
    }
  }

  async function handleDelete(user: AppUserManagement): Promise<void> {
    if (!canWrite) return;
    if (
      !(await confirm({
        title: 'Excluir colaborador permanentemente',
        message:
          `Você está prestes a excluir "${user.name}" (${user.email}).\n\n` +
          'Esta ação é irreversível: não será possível restaurar o colaborador nem o acesso.',
        details: [
          'Login, identidades e preferências de notificação serão excluídos (cascade).',
          'Cadastro do colaborador, dependentes e formação serão excluídos (cascade).',
          'Vínculos com times, papéis e grupos de permissão serão removidos (cascade).',
          'Registros de negócio (oportunidades, tickets, contratos, etc.) permanecem; referências opcionais ao usuário serão limpas (set null) e vínculos obrigatórios serão reatribuídos a outro colaborador ativo.',
        ],
        confirmLabel: 'Excluir permanentemente',
        cancelLabel: 'Cancelar',
        tone: 'danger',
      }))
    ) {
      return;
    }
    try {
      await deleteUser(user.id);
      toast.success('Colaborador excluído.');
      if (editingId === user.id) {
        collapseForm(resetForm);
      }
      await loadData(page);
    } catch (error) {
      const detail =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      toast.error(detail ?? 'Falha ao excluir colaborador.');
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <PageShell>
      <PageHeader
        title="Colaboradores"
        description="Gerencie o perfil de acesso, o cadastro e as equipes dos colaboradores — incluindo o gestor de cada vínculo."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={formTitle}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={editingFormSummary}
          createSummary="Clique para expandir e cadastrar um novo colaborador"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3 space-y-3">
            <div className="erp-form-grid md:grid-cols-2">
              <input
                className="erp-input-compact"
                placeholder="Nome"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
              <input
                className="erp-input-compact"
                placeholder="E-mail"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
              <PasswordInput
                className="erp-input-compact w-full pr-11"
                placeholder={editingId ? 'Nova senha (opcional)' : 'Senha'}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required={!editingId}
              />
            </div>
            <div>
              <p className="mb-2 text-sm font-medium text-secondary-800">Perfil hierárquico</p>
              <select
                className="erp-input-compact max-w-md"
                value={form.accessProfileId}
                onChange={(event) => setForm({ ...form, accessProfileId: event.target.value })}
              >
                <option value="">— Selecione —</option>
                {profiles.map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {profile.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <p className="mb-2 text-sm font-medium text-secondary-800">Grupos de permissões</p>
              <p className="mb-2 text-xs text-secondary-500">
                O colaborador pode ter vários grupos. Em cada módulo, negar tem prioridade sobre permitir.
              </p>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {permissionGroups.map((group) => (
                  <label
                    key={group.id}
                    className="erp-label-inline rounded-md border border-secondary-200 px-3 py-2"
                  >
                    <input
                      type="checkbox"
                      checked={form.permissionGroupIds.includes(group.id)}
                      onChange={() => togglePermissionGroup(group.id)}
                    />
                    <span>{group.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="erp-label-inline">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Ativo
            </label>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-2"
            >
              <button type="submit" className="erp-btn-primary">
                Salvar
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="erp-table-wrap">
        {loading ? (
          <p className="erp-loading">Carregando...</p>
        ) : (
          <div className="erp-table-scroll">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Equipe</th>
                  <th>Gestor</th>
                  <th>Cargo</th>
                  <th>Status</th>
                  <th>Cadastro</th>
                  {canWrite ? <th>Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      {user.teamNames.length > 0 ? user.teamNames.join(', ') : '—'}
                    </td>
                    <td>
                      {user.managerNames.length > 0 ? user.managerNames.join(', ') : '—'}
                    </td>
                    <td>{user.jobTitle ?? '—'}</td>
                    <td>
                      <ActiveStatusBadge active={user.isActive} />
                    </td>
                    <td>
                      <TableActionLink to={`/admin/users/${user.id}/profile`}>Acessar</TableActionLink>
                    </td>
                    {canWrite ? (
                      <td>
                        <div className="flex items-center gap-1">
                          <EditIconButton
                            label="Editar usuário"
                            onClick={() => startEdit(user)}
                          />
                          <DeleteIconButton
                            label="Excluir usuário"
                            onClick={() => void handleDelete(user)}
                          />
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ListPaginationBar
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPrevious={() => setPage((current) => Math.max(1, current - 1))}
        onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
      />
    </PageShell>
  );
}
